import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { TFunction, useTranslation } from 'react-i18next';
import { editDocumentTemplate } from '../store/Supplier.action';
import Loader from '../../../Components/Utility/Loader/Loader';
import { BombiixModal } from '../../../Components/BombiixModal';
import { NewInput } from '../../../Components/PIM/common/NewInput';
import { NewDatePicker } from '../../../Components/PIM/common/NewDatePicker';
import { toStoredCalendarPickerDate } from '../../../Components/helper';

interface EditTemplateForm {
  [key: string]: unknown;
}

interface EditDocumentPayload {
  supplierId: string | undefined;
  title: unknown;
  loggedInUserId: string | undefined;
  expiredAt: unknown;
  docId: unknown;
  section: string;
  action: string;
}

interface OwnProps {
  onOpen: boolean;
  toggle: () => void;
  data: Record<string, unknown> | null | undefined;
  supplierId: string | undefined;
  refreshPage: (refresh: boolean) => void;
}

interface StateProps {
  documentsList: unknown;
  userInfo: { userId?: string } | undefined;
  isCompanyAdminLoggedIn: boolean | undefined;
  isCompanyUserLoggedIn: boolean | undefined;
}

interface DispatchProps {
  editDocumentTemplates: (payload: EditDocumentPayload, transactionMsg: TFunction<'common'>) => Promise<unknown>;
}

interface EditSupplierTemplateState {
  Users: { documentsList: unknown };
  UserDetails: { userInfo?: { userId?: string }; isCompanyAdminLoggedIn?: boolean; isCompanyUserLoggedIn?: boolean };
}

type EditSupplierTemplateProps = OwnProps & StateProps & DispatchProps;

/**
 * Edits a supplier document's name and expiry date.
 * @param {object} props - Component props.
 * @returns {React.ReactElement} The edit modal.
 */
function EditSupplierTemplate({ onOpen, toggle, data, supplierId, userInfo, refreshPage, editDocumentTemplates }: EditSupplierTemplateProps) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<EditTemplateForm>();
  const { t } = useTranslation('common');
  const [expairyDueDate, setExpairyDueDate] = useState<unknown>(null);
  const [showLoader, setShowLoader] = useState(false);
  const loggedInUserId = userInfo?.userId;
  const editDocumentHandler = async (submitedData: EditTemplateForm) => {
    const obj = {
      supplierId,
      title: submitedData['title'],
      loggedInUserId,
      expiredAt: toUtcCalendarDate(expairyDueDate || submitedData['expairyDueDate']),
      docId: submitedData._id,
      section: 'supplierOverview',
      action: 'addEdit',
    };

    setShowLoader(true);
    const res = await editDocumentTemplates(obj, t);
    setShowLoader(false);
    if (res) {
      refreshPage(true);
      toggle();
    }
  };

  useEffect(() => {
    const dataArray = data;
    if (dataArray && Object.keys(dataArray).length) {
      setShowLoader(true);
      Object.entries(dataArray).forEach(([key, value]) => {
        if (key === 'title') {
          setValue('fileName', value);
          setValue('title', value);
        }

        if (key === 'templateName') {
          setValue('fileName', value);
          setValue('templateName', value);
        }
        if (key === 'expiredAt') {
          setExpairyDueDate(data?.['expiredAt'] || null);
          setValue('expairyDueDate', data?.['expiredAt'], {
            shouldValidate: true,
          });
        }

        setValue(key, value, { shouldValidate: true });
      });
    }

    setShowLoader(false);
  }, [data]);

  const handleDate = (date: unknown) => {
    setExpairyDueDate(date);
  };

  return (
    <BombiixModal
      show={onOpen}
      size="lg"
      className="supplier-document-modal"
      title={t('SUPPLIER_DOCUMENT.EDIT_TEMPLATE')}
      onClose={toggle}
      onSave={handleSubmit(editDocumentHandler)}
      saveLabel={t('SUPPLIER_DOCUMENT.UPDATE')}
    >
      {showLoader ? (
        <Loader />
      ) : (
        <div className="partner-form-grid">
          <div className="partner-form-grid__field">
            <NewInput
              label={t('SUPPLIER_DOCUMENT.DOCUMENT_NAME')}
              required
              placeholder={t('SUPPLIER_DOCUMENT.DOCUMENT_NAME')}
              invalid={Boolean(errors['title'])}
              {...register('title', {
                required: true,
                validate: (value) => !!String(value).trim() || 'no whitespaces.',
              })}
            />
            {errors['title'] && <small className="error-message">{t('SUPPLIER_DOCUMENT.DOCUMENT_NAME_REQUIRED')}</small>}
          </div>

          <div className="partner-form-grid__field">
            <NewInput
              label={t('SUPPLIER_DOCUMENT.TEMPLATE_NAME')}
              placeholder={t('SUPPLIER_DOCUMENT.TEMPLATE_NAME')}
              disabled
              {...register('templateName', {
                required: true,
                validate: (value) => !!String(value).trim() || 'no whitespaces.',
              })}
            />
          </div>

          <div className="partner-form-grid__field">
            <span className="bombiix-input__label">{t('SUPPLIER_DOCUMENT.EXPIRY_DATE')}</span>
            <NewDatePicker
              dateFormat="dd/MM/yyyy"
              placeholder={t('SUPPLIER_DOCUMENT.EXPIRY_DATE')}
              value={toDateValue(expairyDueDate)}
              onChange={(date) => handleDate(date)}
              invalid={Boolean(errors['expairyDueDate'])}
            />
            {errors['expairyDueDate'] && <small className="error-message">{t('SUPPLIER_DOCUMENT.EXPIRY_DATE_REQUIRED')}</small>}
          </div>
        </div>
      )}
    </BombiixModal>
  );
}

/**
 * Maps the store slices this modal reads.
 * @param {EditSupplierTemplateState} state - Root store state.
 * @returns {StateProps} The mapped props.
 */
const mapStateToProps = ({ Users, UserDetails }: EditSupplierTemplateState): StateProps => {
  const { documentsList } = Users;
  const { userInfo, isCompanyAdminLoggedIn, isCompanyUserLoggedIn } = UserDetails;
  return {
    documentsList,
    userInfo,
    isCompanyAdminLoggedIn,
    isCompanyUserLoggedIn,
  };
};

const mapStateToDispatch = {
  editDocumentTemplates: editDocumentTemplate,
} as unknown as DispatchProps;

export default connect<StateProps, DispatchProps, OwnProps, EditSupplierTemplateState>(mapStateToProps, mapStateToDispatch)(EditSupplierTemplate);

/**
 * Normalises a stored expiry value (ISO string or Date) into the `Date | null` NewDatePicker expects.
 * A stored string is read back as the calendar day it stands for, the same day the list shows,
 * whether it was saved at UTC midnight or as an older local midnight.
 * @param {unknown} value - Stored expiry value.
 * @returns {Date | null} A valid Date, or null when empty or unparseable.
 */
function toDateValue(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  return toStoredCalendarPickerDate(String(value));
}

/**
 * Turns a picked expiry day into the stored form: UTC midnight of that calendar day. A value that
 * is not a Date (an unchanged stored string) is sent as it came.
 * @param {unknown} value - The expiry value held by the form.
 * @returns {unknown} The value to send, or null when unset.
 */
function toUtcCalendarDate(value: unknown): unknown {
  if (!value) return null;
  if (!(value instanceof Date)) return value;
  if (Number.isNaN(value.getTime())) return null;
  return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())).toISOString();
}
